import type { LucideIcon } from 'lucide-react';
import { ArrowLeftRight, Building2, ChartColumn, Contact, Download, Landmark, Percent, Repeat, TrendingUp, TriangleAlert, Users, Wallet } from 'lucide-react';
import { Link } from 'react-router';
import { useTranslation } from 'react-i18next';
import { Vazio } from '../../componentes/estados';
import { useSessao } from '../../nucleo/sessao';
import { SecoesSalvos } from './salvos';
import { SecaoVisoes } from './visoes';

/** Um cartão da página de entrada: a aba do relatório, o ícone e a permissão da aba (a mesma do `index.tsx`). */
export interface CartaoRelatorio {
  chave: string;
  icone: LucideIcon;
  permissao: string;
  /** E1: a outra permissão que o cartão exige (Pessoas e Folha: a da aba é a do módulo dono, e esta, `relatorios.ver`). */
  exige?: string;
  /** Lote 19: só para quem vê todas as empresas com o consolidado (a mesma regra da aba). */
  todasAsEmpresas?: boolean;
}

/**
 * Fase 2 (W31, 6.2): os relatórios que existem, na ordem do sistema antigo adaptada aos do novo (um cartão por aba). A
 * lista é conferida contra as abas do módulo em `tests/w31-navegacao.test.ts`.
 */
export const CARTOES_RELATORIOS: readonly CartaoRelatorio[] = [
  { chave: 'painel-grupo', icone: Building2, permissao: 'relatorios.ver', todasAsEmpresas: true },
  { chave: 'inadimplencia', icone: TriangleAlert, permissao: 'relatorios.ver' },
  { chave: 'dre', icone: TrendingUp, permissao: 'relatorios.ver' },
  { chave: 'fluxo', icone: ChartColumn, permissao: 'relatorios.ver' },
  { chave: 'extrato', icone: ArrowLeftRight, permissao: 'relatorios.ver' },
  { chave: 'centros', icone: Landmark, permissao: 'relatorios.ver' },
  { chave: 'parceiros', icone: Users, permissao: 'relatorios.ver' },
  { chave: 'mrr', icone: Repeat, permissao: 'relatorios.ver' },
  { chave: 'margem', icone: Percent, permissao: 'relatorios.ver' },
  { chave: 'quadro', icone: Contact, permissao: 'pessoas.ver', exige: 'relatorios.ver' },
  { chave: 'folha', icone: Wallet, permissao: 'folha.ver', exige: 'relatorios.ver' },
  { chave: 'exportacoes', icone: Download, permissao: 'relatorios.exportar' },
];

export const caminhoRelatorio = (chave: string) => `/relatorios/relatorios/${chave}`;

/**
 * Fase 2 (W31, 6.2 e decisão 5 de 05/10): página de entrada de Relatórios, com a estrutura de textos, botões e escolhas do
 * sistema antigo no layout do novo: título "Relatórios" e subtítulo "Análises financeiras e operacionais" (cabeçalho da
 * `Secao`, `relatorios:subtitulo`) e a grade de cartões, um por relatório que existe, com descrição curta. O cartão é um link
 * (teclado e leitor de tela: nome do relatório e a descrição); o interior de cada relatório não muda. Visões gerais, fichas
 * analíticas do antigo ficam para depois (decisão 5). E1 (lote 09/10, item 6): abaixo da grade, "Meus relatórios" e
 * "Compartilhados comigo" (os relatórios salvos que a pessoa pode abrir) e, R37-SPEC-M2, as visões salvas das listas marcadas
 * "Mostrar em Relatórios" (`SecaoVisoes`).
 */
export function PaginaRelatorios() {
  const { t } = useTranslation('relatorios');
  const s = useSessao();
  const cartoes = CARTOES_RELATORIOS.filter((c) => s.pode(c.permissao) && (!c.exige || s.pode(c.exige)) && (!c.todasAsEmpresas || s.todasAsEmpresas));
  if (!cartoes.length) return <Vazio />;
  return (
    <>
    <ul className="grid grid-cols-1 gap-3 sm:grid-cols-2" data-pagina-relatorios="">
      {cartoes.map((c) => {
        const Icone = c.icone;
        const nome = t(`abas.${c.chave}`);
        return (
          <li key={c.chave} className="min-w-0">
            <Link
              to={caminhoRelatorio(c.chave)}
              data-cartao-relatorio={c.chave}
              className="flex h-full min-w-0 items-start gap-4 rounded-xl border border-borda bg-sup-1 p-4 hover:bg-sup-2"
            >
              <span className="shrink-0 rounded-lg bg-acento-suave p-3 text-acento-forte" aria-hidden>
                <Icone className="size-5" />
              </span>
              <span className="min-w-0 flex-1">
                <span className="block text-base font-semibold break-words text-tinta-1">{nome}</span>
                <span className="mt-1 block text-sm break-words text-tinta-2">
                  {t(`pagina.descricoes.${c.chave}`)}
                </span>
              </span>
            </Link>
          </li>
        );
      })}
    </ul>
    <SecoesSalvos />
    <SecaoVisoes />
    </>
  );
}
